import { LayoutDashboard, PanelLeftClose, PanelLeftOpen, Settings } from "lucide-mui";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client";
import VaykenLogo from "../components/VaykenLogo";
import ContextRow from "../components/ContextRow";
import RunAgentDialog from "../components/agent/RunAgentDialog.tsx";
import { userCanManageOrgApps, userHasGlobalScope } from "../auth/scopes";
import { FEATURE_MICRO_APPS, useOrgFeature } from "../auth/features.ts";
import { useAuth } from "../contexts/AuthContext.tsx";
import { useOrganization } from "../contexts/OrganizationContext.tsx";
import { useTenant } from "../contexts/TenantContext.tsx";
import AccountMenu from "./sidebar/AccountMenu";
import FlyoutLinkList from "./sidebar/FlyoutLinkList";
import SidebarNavItem from "./sidebar/SidebarNavItem";
import WorkspaceSwitcher from "./sidebar/WorkspaceSwitcher";
import { tenantNavItems, visibleItems } from "./sidebar/navItems";
import { areaContains, useActiveNavArea, useNavAreas, type NavAreaId } from "./sidebar/navAreas";
import SectionColumn from "./sidebar/SectionColumn";
import { NAV_ICON_SIZE, SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED, rowPaddingFor, sidebarSx } from "./sidebar/styles";

// Desktop only: the sidebar always starts collapsed on a narrow screen.
const COLLAPSED_STORAGE_KEY = "vayken_nav_collapsed";
// Read until everyone has stored a choice under the new key.
const LEGACY_COLLAPSED_STORAGE_KEY = "scaleupxq_nav_collapsed";

function AppLayout() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { selectedOrgId } = useOrganization();
  const { selectedTenantId, tenants, tenantPath } = useTenant();
  // The whole Apps section follows the org's micro-apps feature flag.
  const hasMicroApps = useOrgFeature(FEATURE_MICRO_APPS);
  const canManageApps = hasMicroApps && userCanManageOrgApps(user, selectedOrgId);
  // The only query-driven nav section: micro apps are published at runtime, so
  // they cannot live in a static array like every other entry here.
  const { data: microApps } = useQuery({
    queryKey: keys.microApp.nav(selectedOrgId),
    queryFn: () => api.getMicroApps({ organization_id: selectedOrgId, enabled_only: true }),
    enabled: hasMicroApps && userHasGlobalScope(user, "apps:read"),
    staleTime: 60_000,
  });
  const navMicroApps = hasMicroApps
    ? (microApps ?? []).filter((a) => a.show_in_nav && a.current_version_id)
    : [];
  const tenantNameById = new Map(tenants.map((t) => [t.id, t.name]));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const areas = useNavAreas();
  const activeArea = useActiveNavArea(areas);
  // Inside Explore, Organization, Platform or Account the area's pages get a
  // column of their own and the sidebar narrows to icons to make room for it;
  // a phone keeps the flyouts.
  const sectionArea = isMobile ? null : activeArea;
  // Expanding the sidebar inside an area holds until the user leaves it.
  const [expandedInArea, setExpandedInArea] = useState<NavAreaId | null>(null);
  const [desktopCollapsed, setDesktopCollapsed] = useState(
    () =>
      (localStorage.getItem(COLLAPSED_STORAGE_KEY) ??
        localStorage.getItem(LEGACY_COLLAPSED_STORAGE_KEY)) === "1",
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const collapsed = isMobile
    ? !mobileOpen
    : sectionArea
      ? expandedInArea !== sectionArea.id
      : desktopCollapsed;
  const toggleCollapsed = () => {
    if (isMobile) {
      setMobileOpen((open) => !open);
      return;
    }
    if (sectionArea) {
      setExpandedInArea(collapsed ? sectionArea.id : null);
      return;
    }
    const next = !desktopCollapsed;
    setDesktopCollapsed(next);
    localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? "1" : "0");
  };
  const [pinnedFlyoutId, setPinnedFlyoutId] = useState<string | null>(null);
  const [runAgentOpen, setRunAgentOpen] = useState(false);
  const closeFlyout = () => setPinnedFlyoutId(null);

  // Close any pinned flyout when the sidebar collapses/expands to avoid stale anchors
  useEffect(() => {
    setPinnedFlyoutId(null);
  }, [collapsed]);

  const area = (id: NavAreaId) => areas.find((a) => a.id === id)!;
  const footerRow = (id: NavAreaId, flyoutWidth?: number) => {
    const a = area(id);
    if (a.sections.length === 0) return null;
    return (
      <ContextRow
        label={a.label}
        icon={a.icon}
        collapsed={collapsed}
        selected={areaContains(a, pathname)}
        flyoutContent={<FlyoutLinkList sections={a.sections} onNavigate={closeFlyout} />}
        flyoutId={id}
        flyoutWidth={flyoutWidth}
        pinnedFlyoutId={pinnedFlyoutId}
        setPinnedFlyoutId={setPinnedFlyoutId}
        placement="right-end"
      />
    );
  };

  const drawerWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
  const widthTransition = theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  });

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: widthTransition,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: widthTransition,
            bgcolor: "background.paper",
            borderRight: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            ...sidebarSx,
          },
        }}
      >
        <Box
          component={Link}
          to={tenantPath("")}
          aria-label="Home"
          sx={{
            display: "flex",
            alignItems: "center",
            height: 52,
            flexShrink: 0,
            px: `calc(8px + ${rowPaddingFor(28)})`,
          }}
        >
          <VaykenLogo height={collapsed ? 28 : 26} markOnly={collapsed} />
        </Box>

        <Box sx={{ flexShrink: 0, pb: 1 }}>
          <WorkspaceSwitcher
            collapsed={collapsed}
            pinnedFlyoutId={pinnedFlyoutId}
            setPinnedFlyoutId={setPinnedFlyoutId}
          />
        </Box>

        <Box component="nav" aria-label="Main" sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
          {selectedTenantId && (
            <List disablePadding>
              {visibleItems(tenantNavItems, user, selectedOrgId).map((item) => (
                <SidebarNavItem
                  key={item.path}
                  item={item}
                  to={tenantPath(item.path)}
                  exact={item.path === "" || item.path === "/settings"}
                  collapsed={collapsed}
                  onAction={(action) => {
                    if (action !== "run-agent") return;
                    setMobileOpen(false);
                    setRunAgentOpen(true);
                  }}
                />
              ))}
            </List>
          )}

          {(navMicroApps.length > 0 || canManageApps) && (
            <List
              disablePadding
              sx={{ mt: collapsed ? 1 : 2 }}
              subheader={
                collapsed ? undefined : (
                  <ListSubheader sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: "12px !important" }}>
                    Apps
                    {canManageApps && (
                      <Tooltip title="Manage apps" disableInteractive>
                        <IconButton
                          size="small"
                          component={Link}
                          to="/settings/micro-apps"
                          aria-label="Manage apps"
                          sx={{ color: "inherit", p: 0.5, "& svg": { fontSize: 14 } }}
                        >
                          <Settings />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListSubheader>
                )
              }
            >
              {navMicroApps.map((app) => (
                <SidebarNavItem
                  key={app.id}
                  collapsed={collapsed}
                  item={{
                    label: app.title,
                    path: `/apps/${app.slug}`,
                    icon: <LayoutDashboard />,
                    secondary:
                      app.tenant_id === null
                        ? undefined
                        : tenantNameById.get(app.tenant_id) ?? "Unknown",
                  }}
                />
              ))}
            </List>
          )}
        </Box>

        <Box sx={{ flexShrink: 0, borderTop: "1px solid", borderColor: "divider", pt: 1 }}>
          {footerRow("explore")}
          {footerRow("organization")}
          {footerRow("platform", 280)}
          <AccountMenu
            collapsed={collapsed}
            pinnedFlyoutId={pinnedFlyoutId}
            setPinnedFlyoutId={setPinnedFlyoutId}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: collapsed ? "center" : "flex-end",
              px: 1,
              pt: 0.5,
              pb: 1,
            }}
          >
            <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right" disableInteractive>
              <IconButton
                onClick={toggleCollapsed}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  color: "text.secondary",
                  "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                  "& svg": { fontSize: NAV_ICON_SIZE },
                }}
              >
                {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Drawer>

      {sectionArea && <SectionColumn area={sectionArea} />}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
        <Outlet />
      </Box>
      {runAgentOpen && <RunAgentDialog onClose={() => setRunAgentOpen(false)} />}
    </Box>
  );
}

export default AppLayout;
